import { useCallback, useState } from 'react';
import { ProposalOwner, ProposedAction, ProposedActionStatus } from '../chat/types';

type OrphanDecision = {
  status: ProposedActionStatus;
  failureReason: string | null;
  owner?: ProposalOwner;
};

/**
 * Orphan-recovered proposal widgets carry no message id, so `markProposalDecided` cannot reach
 * them: only the pending-proposals poll retires them. That poll is disabled while a turn is
 * streaming — which is exactly when the resume message runs after a decision — so the widget
 * would keep live approve/deny buttons over an already-decided proposal, and a second click
 * would 409 against the server-side guard. Remembering the decision here retires the widget as
 * soon as it lands, whatever the poll is doing.
 */
export function useOrphanProposalDecisions() {
  const [decisions, setDecisions] = useState<Record<string, OrphanDecision>>({});

  const recordOrphanDecision = useCallback(
    (
      proposalId: string,
      status: ProposedActionStatus,
      failureReason: string | null = null,
      owner?: ProposalOwner,
    ) =>
      setDecisions(previous => ({
        ...previous,
        [proposalId]: { status, failureReason, owner },
      })),
    [],
  );

  const applyOrphanDecisions = useCallback(
    (proposals: ProposedAction[]): ProposedAction[] =>
      proposals.map(proposal => {
        const decided = decisions[proposal.proposalId];
        if (!decided) return proposal;
        return {
          ...proposal,
          ...decided.owner,
          status: decided.status,
          failureReason: decided.failureReason ?? proposal.failureReason,
        };
      }),
    [decisions],
  );

  return { recordOrphanDecision, applyOrphanDecisions };
}
